import { extractApiErrorCodeAsync } from '@/utils/api/mapApiError'
import { extractServerViolationsAsync } from '@/utils/api/serverViolations'

// BAT-1107: switching the favourite (club or athlete) follows «Выбор Favorite
// Club» §9 / §14, enforced by the gateway (BAT-1100): the first switch is
// free, later ones once per 30 days (unless the current favourite is
// blocked), never while tasks are reserved / in progress. BAT-1180: a
// favourite from the sign-up referral link has no free first switch — it
// opens 30 days after the sign-up (reason «referral», AG-307).

export type FavoriteChangeBlockReason = 'cooldown' | 'active_tasks' | 'referral'

/** Why the switch is refused now; availableFrom for the cooldown and the
 * referral lock. */
export type FavoriteChangeBlock = {
  reasons: FavoriteChangeBlockReason[]
  availableFrom?: string
}

/** GET /api/v2/users/me/favorite-change. */
export type FavoriteChangeAvailability = {
  available?: boolean
  reason?: string
  available_from?: string
}

export const FAVORITE_CHANGE_KEY = 'favorite_change'

const CODE_COOLDOWN = 'AG-304'
const CODE_ACTIVE_TASKS = 'AG-305'
const CODE_REFERRAL_LOCK = 'AG-307'

/** Reasons that open on a date. */
const DATED_REASONS: ReadonlySet<string> = new Set(['cooldown', 'referral'])

const isReason = (value?: string): value is FavoriteChangeBlockReason =>
  value === 'cooldown' || value === 'active_tasks' || value === 'referral'

/** null — the switch is allowed. An unknown reason still blocks it. */
export function blockFromAvailability(
  availability: FavoriteChangeAvailability | null | undefined
): FavoriteChangeBlock | null {
  if (!availability || availability.available !== false) {
    return null
  }
  const reason = availability.reason
  return {
    reasons: isReason(reason) ? [reason] : [],
    availableFrom:
      reason && DATED_REASONS.has(reason)
        ? availability.available_from
        : undefined
  }
}

/**
 * PUT /users/me refused the switch: AG-304 (cooldown) or AG-307 (referral
 * lock, BAT-1180) — the date in violations[0].description — or AG-305
 * (tasks in work). null for any other error.
 */
export async function blockFromError(
  error: unknown
): Promise<FavoriteChangeBlock | null> {
  const code = await extractApiErrorCodeAsync(error)
  if (code === CODE_ACTIVE_TASKS) {
    return { reasons: ['active_tasks'] }
  }
  if (code === CODE_COOLDOWN || code === CODE_REFERRAL_LOCK) {
    const violations = await extractServerViolationsAsync(error)
    const date = violations[0]?.description
    return {
      reasons: [code === CODE_REFERRAL_LOCK ? 'referral' : 'cooldown'],
      availableFrom: date && !Number.isNaN(Date.parse(date)) ? date : undefined
    }
  }
  return null
}

/** The date the switch opens, in the page language («November 14, 2026»). */
export function formatAvailableFrom(
  iso: string | undefined,
  locale: string
): string | null {
  if (!iso) {
    return null
  }
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) {
    return null
  }
  return new Intl.DateTimeFormat(locale, {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  }).format(date)
}

type BlockedTranslator = (
  key: 'toastActiveTasks' | 'toastCooldown' | 'title',
  values?: { date: string }
) => string

/** One-line reason for a toast (profile edit, Requested Club). */
export function blockToastTitle(
  block: FavoriteChangeBlock,
  locale: string,
  t: BlockedTranslator
): string {
  if (block.reasons.includes('active_tasks')) {
    return t('toastActiveTasks')
  }
  const date = formatAvailableFrom(block.availableFrom, locale)
  return date ? t('toastCooldown', { date }) : t('title')
}
